import React, { useState, useEffect } from "react";
import ModernSidebar from "./ModernSidebar";
import ModernHeader from "./ModernHeader";
import FinancialSummary from "./FinancialSummary";
import QuickStats from "./QuickStats";
import CashFlow from "./CashFlow";
import RecentActivities from "./RecentActivities";
import { AES, enc } from "crypto-js";
import { BASE_URL } from "../../screens/Dashboard/CommonApi";
import { getActiveOrganizationHeaderValue } from "../../utils/organizationContext";
import { getTimeGreeting } from "../../utils/timeGreeting";
import "./ModernDashboard.css";

const defaultMetrics = {
  totalReceivables: 0,
  totalPayables: 0,
  receivablesGrowth: 0,
  payablesGrowth: 0,
  receivablesCurrent: 0,
  receivablesOverdue: 0,
  payablesCurrent: 0,
  payablesOverdue: 0,
  currentInvoices: 0,
  overdueInvoices: 0,
  currentBills: 0,
  overdueBills: 0,
  totalInvoices: 0,
  totalPurchases: 0,
  totalItems: 0,
  totalCustomers: 0,
  cashFlow: [
    { date: "Jan", incoming: 0, outgoing: 0 },
    { date: "Feb", incoming: 0, outgoing: 0 },
    { date: "Mar", incoming: 0, outgoing: 0 },
    { date: "Apr", incoming: 0, outgoing: 0 },
    { date: "May", incoming: 0, outgoing: 0 },
    { date: "Jun", incoming: 0, outgoing: 0 },
    { date: "Jul", incoming: 0, outgoing: 0 },
    { date: "Aug", incoming: 0, outgoing: 0 },
    { date: "Sep", incoming: 0, outgoing: 0 },
    { date: "Oct", incoming: 0, outgoing: 0 },
    { date: "Nov", incoming: 0, outgoing: 0 },
    { date: "Dec", incoming: 0, outgoing: 0 },
  ],
};

const decryptUniqueId = () => {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  return encryptedUniqueId
    ? AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8)
    : "";
};

const postMetric = async (endpoint, body = {}) => {
  const jwtToken = localStorage.getItem("jwtToken");
  const uniqueId = decryptUniqueId();
  if (!jwtToken || !uniqueId) return null;

  const response = await fetch(BASE_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({ uniqueId, ...body }),
  });
  return response.json();
};

const toNumber = (value) => {
  const parsed = Number(String(value ?? 0).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value.flat(Infinity).filter((row) => row && typeof row === "object");
  if (!value || typeof value !== "object") return [];
  const rows = Object.values(value).find(Array.isArray);
  return rows ? rows.flat(Infinity).filter((row) => row && typeof row === "object") : [];
};

const parseDashboardDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  const indianDate = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  const normalized = indianDate
    ? `${indianDate[3]}-${indianDate[2].padStart(2, "0")}-${indianDate[1].padStart(2, "0")}`
    : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const summarizeReceivablesFromInvoices = (rows = []) => {
  const seen = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return rows.reduce(
    (summary, invoice, index) => {
      const invoiceKey = invoice.invoiceNumber || invoice.invoice || invoice.id || index;
      if (seen.has(invoiceKey)) return summary;
      seen.add(invoiceKey);

      const status = String(invoice.status || "").toLowerCase();
      if (["cancelled", "canceled", "void", "draft"].includes(status)) return summary;

      const amount = toNumber(invoice.receivables ?? invoice.totalDue ?? invoice.balanceDue ?? 0);
      if (amount <= 0) return summary;

      const dueDate = parseDashboardDate(invoice.dueDate || invoice.invoiceDueDate);
      if (dueDate && dueDate < today) {
        summary.overdueAmount += amount;
        summary.overdueCount += 1;
      } else {
        summary.currentAmount += amount;
        summary.currentCount += 1;
      }
      return summary;
    },
    { currentAmount: 0, overdueAmount: 0, currentCount: 0, overdueCount: 0 }
  );
};

const ModernDashboard = ({ navigation }) => {
  const routeState = navigation?.state;
  const [userData, setUserData] = useState(null);
  const [dashboardData, setDashboardData] = useState(defaultMetrics);
  const [recentActivity, setRecentActivity] = useState({});
  const [activityLoading, setActivityLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("dashboard");
  const [activeOrganizationKey, setActiveOrganizationKey] = useState(() => getActiveOrganizationHeaderValue());

  // Get active route
  useEffect(() => {
    if (routeState?.routes?.[routeState.index]) {
      setActiveKey(routeState.routes[routeState.index].key);
    }
  }, [routeState]);

  useEffect(() => {
    const syncActiveOrganization = () => {
      setActiveOrganizationKey(getActiveOrganizationHeaderValue());
    };

    window.addEventListener("storage", syncActiveOrganization);
    window.addEventListener("zarp:organization-change", syncActiveOrganization);
    return () => {
      window.removeEventListener("storage", syncActiveOrganization);
      window.removeEventListener("zarp:organization-change", syncActiveOrganization);
    };
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setActivityLoading(true);
      setDashboardData(defaultMetrics);
      setRecentActivity({});
      try {
        const decryptedUniqueId = decryptUniqueId();
        const jwtToken = localStorage.getItem("jwtToken");
        const userId = localStorage.getItem("userId");

        // The route can render briefly while Login is refreshing a session.
        // Do not send unauthenticated dashboard requests during that interval.
        if (!jwtToken || !decryptedUniqueId) return;

        const profileRequest = fetch(BASE_URL + "/fetchUserProfile", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ uniqueId: decryptedUniqueId, userId }),
        }).then((response) => response.json());

        const [
          profile,
          invoices,
          purchases,
          customers,
          items,
          receivables,
          payables,
          graph,
          quotations,
          expenses,
          payments,
        ] = await Promise.allSettled([
          profileRequest,
          postMetric("/fetchInvoice"),
          postMetric("/fetchPurchaseOrder", { fromDate: "", toDate: "" }),
          postMetric("/getAllCustomerName"),
          postMetric("/getAllItemData", { vendorId: "" }),
          postMetric("/totalReceivables"),
          postMetric("/fetchTotalPaybles", { fromData: "", toDate: "" }),
          postMetric("/fetchForGraph"),
          postMetric("/fetchQuote"),
          postMetric("/fetchExpenses"),
          postMetric("/fetchPaymentReceived2"),
        ]);

        const invoiceRows = normalizeRows(invoices.value?.responseMessage);
        const receivablesSummary = summarizeReceivablesFromInvoices(invoiceRows);
        const receivablesCurrent = invoiceRows.length
          ? receivablesSummary.currentAmount
          : toNumber(receivables.value?.tocurrect);
        const receivablesOverdue = invoiceRows.length
          ? receivablesSummary.overdueAmount
          : toNumber(receivables.value?.tooverDue);

        setRecentActivity({
          invoice: invoiceRows,
          customer: normalizeRows(customers.value?.responseMessage),
          quotation: normalizeRows(quotations.value?.responseMessage),
          expense: normalizeRows(expenses.value?.responseMessage),
          payment: normalizeRows(payments.value?.responseMessage),
        });

        const profileData = profile.value?.responseMessage || profile.value?.message;
        if (profile.status === "fulfilled" && profileData) {
          setUserData(profileData);
        }

        const monthlySales = graph.status === "fulfilled" && graph.value
          ? Object.values(graph.value).map((month, index) => ({
              date: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][index] || `M${index + 1}`,
              incoming: toNumber(month?.totalSales),
              outgoing: 0,
            }))
          : defaultMetrics.cashFlow;

        setDashboardData({
          ...defaultMetrics,
          totalInvoices: toNumber(invoices.value?.responseMessage1),
          totalPurchases: toNumber(purchases.value?.responseMessage1),
          totalCustomers: toNumber(customers.value?.responseMessage1),
          totalItems: toNumber(items.value?.rowCount),
          receivablesCurrent,
          receivablesOverdue,
          totalReceivables: receivablesCurrent + receivablesOverdue,
          payablesCurrent: toNumber(payables.value?.current),
          payablesOverdue: toNumber(payables.value?.overDue),
          totalPayables: toNumber(payables.value?.current) + toNumber(payables.value?.overDue),
          currentInvoices: invoiceRows.length ? receivablesSummary.currentCount : 0,
          overdueInvoices: invoiceRows.length ? receivablesSummary.overdueCount : toNumber(invoices.value?.responseMessage1),
          cashFlow: monthlySales.length ? monthlySales : defaultMetrics.cashFlow,
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setActivityLoading(false);
      }
    };

    fetchUserData();
  }, [activeOrganizationKey]);

  return (
    <div className="zarp-dashboard-shell">
      <ModernSidebar navigation={navigation} activeKey={activeKey} />

      <div className="zarp-dashboard-main">
        <ModernHeader
          userName={userData?.name || "Admin"}
          navigation={navigation}
        />

        <main className="zarp-dashboard-content">
          <div className="zarp-mobile-greeting">
            <h1>{getTimeGreeting()}, {userData?.name || "Admin"}</h1>
            <p>Here's what's happening with your business today.</p>
          </div>

          <FinancialSummary data={dashboardData} />
          <QuickStats navigation={navigation} data={dashboardData} />

          <div className="zarp-dashboard-grid">
            <div className="zarp-chart-column">
              <CashFlow data={dashboardData.cashFlow} />
            </div>
            <RecentActivities records={recentActivity} loading={activityLoading} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ModernDashboard;
